import React, { useState, useCallback, useMemo, useContext, useRef } from "react";
import { changeEgovUrl } from "@/utils/tools";
import IconFont from "@/components/icon";
import icon_search from "@/assets/img/icon_search.png";
import iconDefault from "@/assets/img/default_avatar_1.png";
import "./index.less";
import "@/assets/less/common.less";
import { getAvatarGradient } from "./gradient_generator";
import { Avatar as ThirdAvatar } from "@jd/focus-desktop-comps";
import { UserState } from "@/models/user";
import { connect } from "dva";
import { getRealConfig } from "@/utils/tools";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";

enum nameType {
  others = 0,
  enOneWord = 1,
  enTwoWords = 2,
  zh = 3
}


const zhPattern = /[\u4E00-\u9FA5]+/;
const enOneWordPattern = /[A-Za-z0-9]+/;
const enTwoWordsPattern = /[A-Za-z0-9]+[$][A-Za-z0-9]+/;
const defaultSize = 36;

function applyRule(name: string, type: string) {
  if (type === "team") {
    return teamRule(name);
  }
  return defaultRule(name);
}

function teamRule(name: string) {
  // 1. 纯汉字 - 从头到尾都是汉字
  const pureChinesePattern = /^[\u4E00-\u9FA5]+$/;
  // 2. 纯英文 - 从头到尾都是英文字母
  const pureEnglishPattern = /^[A-Za-z]+$/;
  // 3. 纯数字 - 从头到尾都是数字
  const pureNumberPattern = /^[0-9]+$/;
  // 4. 中文开头 - 以汉字开头的任意文本
  const chineseFirstPattern = /^[\u4E00-\u9FA5]/;
  // 5. 英文开头 - 以英文字母开头的任意文本
  const englishFirstPattern = /^[A-Za-z]/;
  // 6. 纯符号 - 只包含符号，不包含中文、英文字母和数字
  const pureSymbolPattern = /^[^\u4E00-\u9FA5A-Za-z0-9\s]+$/;

  const filteredName = filterTeamName(name);
  let str;
  if (pureChinesePattern.test(filteredName) || chineseFirstPattern.test(filteredName) || pureNumberPattern.test(filteredName)) {
    str = filteredName.slice(0, 2);
  } else if (pureEnglishPattern.test(filteredName) || englishFirstPattern.test(filteredName)) {
    str = filteredName.slice(0, 1);
  } else if (pureSymbolPattern.test(filteredName)) {
    str = "租户";
  }
  return str || "租户";
}

function defaultRule(name: string) {
  // 1. 纯汉字 - 从头到尾都是汉字
  const pureChinesePattern = /^[\u4E00-\u9FA5]+$/;
  // 3. 数字开头 
  const numberFirstPattern = /^[0-9]/;
  // 4. 中文开头 - 以汉字开头的任意文本
  const chineseFirstPattern = /^[\u4E00-\u9FA5]/;
  // 5. 英文开头 - 以英文字母开头的任意文本
  const englishFirstPattern = /^[A-Za-z]/;
  // 6. 纯符号 - 只包含符号，不包含中文、英文字母和数字
  const pureSymbolPattern = /^[^\u4E00-\u9FA5A-Za-z0-9\s]+$/;

  const filteredName = filterName(name);
  let str;
  if (pureChinesePattern.test(filteredName) || chineseFirstPattern.test(filteredName)) {
    str = filteredName.slice(-2);
  } else if (englishFirstPattern.test(filteredName) || numberFirstPattern.test(filteredName)) {
    str = filteredName.slice(0, 1);
  } else if (pureSymbolPattern.test(filteredName)) {
    str = "用户";
  }
  return str || "用户";
}

function filterTeamName(name: string) {
  return name
    .trim()
    .split("")
    .filter((char: string) => {
      return !/\s/.test(char); // 只过滤掉空格，保留所有其他字符
    })
    .join("");
}

function filterName(name: string) {
  return name
    .trim()
    .split("")
    .filter((char: string) => {
      return zhPattern.test(char) || enOneWordPattern.test(char) || /\s/.test(char);
    })
    .join("");
}

export function Avatar1(props: {
  size?: number;
  borderRadius?: number;
  styleObj?: any;
  className?: string;
  src?: string;
  name?: string;
  defaultImg?: string;
  onClick?: (e?: any) => void;
  fullName?: boolean;
  tag?: string;
  searchIcon?: boolean;
  onLoadError?: (e?: any) => void;
  avatarMask?: boolean;
  type?: string;
  userBgColors?: string[];
  groupBgColors?: string[];
}): any {
  const {
    styleObj,
    src,
    name,
    className,
    onClick,
    onLoadError,
    size,
    borderRadius,
    fullName,
    searchIcon,
    tag,
    type = "user",
    avatarMask = true,
    userBgColors = ["#0073FF,#0073FF"],
    groupBgColors = ["#0049F6,#0049F6"],
    ...other
  } = props;
  const { isWebpSupported } = useContext(GlobalContext);

  const tagZoom = useMemo(() => {
    try {
      let avatarWidth =
        Number(String(styleObj?.width || size || defaultSize).replace("px", "")) || 50;
      if (isNaN(avatarWidth)) {
        avatarWidth = 50;
      }
      return avatarWidth / 80;
    } catch (error) {
      return 0.5;
    }
  }, [size, styleObj]);

  const divAvatarNode = useMemo(() => {
    if (name) {
      // const displayName = fullName ? name : name[0];
      // const displayName = name.length >= 2 ? name.slice(-2) : name;
      const displayName = fullName ? name : applyRule(name, type);
      const gradientConfigs = type === 'group' ? groupBgColors : userBgColors;
      return (
        <div
          className={`me-avatar ${avatarMask && "avatar-mask"} ${className || ""}`}
          style={{
            width: size || defaultSize,
            height: size || defaultSize,
            borderRadius: 6,
            fontSize: size || 13,
            display: "flex",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            outline: 0,
            position: "relative",
            background: getAvatarGradient(displayName, gradientConfigs),
            // fontWeight: "bold",  // 与服务端同步，不加粗字体
            ...styleObj
          }}
          onClick={onClick}
          {...other}
        >
          {type === 'group' ? <IconFont color="#fff" style={{ fontSize: '18px' }} type="iconFrame12" /> : displayName}
          {tag && (
            <div
              className="avatar-tag-mask"
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                textAlign: "center",
                lineHeight: "24px",
                right: 0,
                zoom: tagZoom,
                whiteSpace: "nowrap",
                fontSize: "16px",
                backgroundColor: "#ED6A0C",
                color: "#ffffff"
              }}
            >
              {tag}
            </div>
          )}
        </div>
      );
    }
    if (searchIcon) {
      return (
        <div
          className={`me-avatar ${className || ""}`}
          style={{
            width: size || defaultSize,
            height: size || defaultSize,
            borderRadius: 6,
            fontSize: 16,
            display: "flex",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            outline: 0,
            ...styleObj
          }}
          onClick={onClick}
          {...other}
        >
          <img src={icon_search} alt="" width="15px" />
          {/* <IconFont type="iconviewlarger" style={{ color: '#ffffff' }} /> */}
        </div>
      );
    }
    return null;
  }, [name, searchIcon, fullName, className, size, styleObj, onClick, other, tag, tagZoom]);

  const imgAvatarNode = useMemo(() => {
    let src1 =
      src && (src.includes("downloadAuthn") ? src.replace("downloadAuthn", "download") : src);
    return (
      <div
        draggable="false"
        className="me-avatar"
        style={{
          background: "none"
        }}
      >
        <img
          onDragStart={(e) => {
            return false;
          }}
          className={`${className} user-card-avatar-remote avatar-img-no-drag ${avatarMask && "avatar-mask"}`}
          style={{
            width: size || defaultSize,
            height: size || defaultSize,
            borderRadius: 6,
            backgroundSize: "cover",
            backgroundColor: "transparent",
            overflow: "hidden",
            display: "block",
            backgroundPositionY: "center",
            outline: 0,
            // backgroundImage: `url(${changeEgovUrl(src1)}${
            //     !isWebpSupported && (src1 || '').indexOf('?') > -1 ? '&imageType=png' : ''
            // })`,
            ...styleObj
          }}
          src={`${changeEgovUrl(src1)}${!isWebpSupported && (src1 || "").indexOf("?") > -1 ? "&imageType=png" : ""
            }`}
          onClick={onClick}
          onError={(e: any) => {
            if (e.target) {
              e.target.src = iconDefault;
            }
            if (onLoadError) {
              onLoadError();
            }
          }}
        />
        {tag && (
          <div
            className="avatar-tag-mask"
            style={{
              position: "absolute",
              bottom: 0,
              textAlign: "center",
              // borderRadius: "0 0 6px 6px",
              left: 0,
              lineHeight: "24px",
              right: 0,
              whiteSpace: "nowrap",
              zoom: tagZoom,
              fontSize: "16px",
              backgroundColor: "#ED6A0C",
              color: "#ffffff"
            }}
          >
            {tag}
          </div>
        )}
      </div>
    );
  }, [src, className, size, styleObj, isWebpSupported, onClick, tag, tagZoom, onLoadError]);
  let filteredSrc = "";
  if (src) {
    let newSrc = changeEgovUrl(src);
    filteredSrc = filterUselessImg(newSrc);
  }
  if (!filteredSrc) {
    return divAvatarNode;
  } else {
    return divAvatarNode;
  }
}

export function filterUselessImg(imgUrl: string) {
  if (imgUrl.includes("ebook.avatar.getDefaultAvatar")) {
    return "";
  }

  return imgUrl;
}

// 对外部库 Avatar 进行二次封装：预处理 src，其他参数透传
export type ThirdAvatarProps = React.ComponentProps<typeof ThirdAvatar> & {
  src?: string;
};

const DefaultAvatar: React.FC<ThirdAvatarProps> = (props) => {
  const { teamConfig } = useContext<GlobalContextConfig>(GlobalContext);
  console.log("GlobalContext===teamConfig", teamConfig);
  
  const { src, ...rest } = props;

  if (teamConfig?.personalAvatarColor) {
    // 读取teamConfig中的头像颜色配置
    const personalAvatarColor = JSON.parse(getRealConfig(teamConfig, {}, "personalAvatarColor"));
    const personalAvatarColorList = personalAvatarColor.personalAvatorColorList.filter((e: any) => e.selected === 1);
    const groupAvatarColor = JSON.parse(getRealConfig(teamConfig, {}, "groupAvatarColor"));
    const groupAvatarColorList = groupAvatarColor.groupAvatarColorList.filter((e: any) => e.selected === 1);
    const userBgColors = personalAvatarColorList.map((e: any) => e.colorCode);
    const groupBgColors = groupAvatarColorList.map((e: any) => e.colorCode);
    // console.log("userBgColors", userBgColors, src);
    return <ThirdAvatar userBgColors={userBgColors} groupBgColors={groupBgColors} src="" {...rest} />;
  }
  return <ThirdAvatar src="" {...rest} />;
};

// function mapStateToProps({ user }: { user: UserState }) {
//   return {
//     teamConfig: user?.teamConfig
//   };
// }
// 采用兜底头像时才注入 teamConfig，获取头像颜色配置 ps.这种方法不行，上下文有可能没注入user
// const ConnectedDefaultAvatar = connect(mapStateToProps)(defaultAvatar);


export const Avatar: React.FC<ThirdAvatarProps> = (props) => {
  const { src, ...rest } = props;
  let processedSrc = src || "";
  if (processedSrc) {
    const newSrc = changeEgovUrl(processedSrc);
    processedSrc = filterUselessImg(newSrc);
    return <ThirdAvatar src={processedSrc || ""} {...rest} />;
  } else {
    // 采用兜底头像
    return <DefaultAvatar src="" {...rest} />;
  }
};




